import React, { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';

import {
  formatAlertHour,
  intentFromCategorySlug,
  intentLabel,
} from '../../config/spendAlertIntents';
import { useAuth } from '../../contexts/AuthContext';
import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import { useBillingActions } from '../../hooks/useBillingActions';
import { ApiError } from '../../services/apiClient';
import {
  deleteSpendAlert,
  listSpendAlerts,
  patchSpendAlert,
  suggestSpendAlerts,
  type ChannelReadiness,
  type SpendAlert,
  type SpendAlertSuggestion,
} from '../../services/spendAlertService';
import {
  notifySpendAlertsChanged,
  subscribeSpendAlertsChanged,
} from '../../services/spendAlertsBus';
import {
  AnalyzeQuotaModal,
  type AnalyzeQuotaInfo,
} from '../billing/AnalyzeQuotaModal';
import { GlassButton } from '../ui/GlassButton';
import { seedFromSuggestion, SetSpendAlertModal, type SpendAlertModalSeed } from './SetSpendAlertModal';

type Props = {
  /** Category chip context for “set alert” seed. */
  categorySlug?: string;
  categoryLabel?: string;
};

/**
 * Daily spend companion: active alerts (edit / pause / cancel) + pattern ideas.
 * Setting alerts is Pro-only; free users see a teaser + upgrade gate.
 */
export function SpendAlertSuggestions({
  categorySlug = 'all',
  categoryLabel = 'All spending',
}: Props) {
  const { isAuthenticated, user } = useAuth();
  const { upgradeToPro, busy: upgradeBusy } = useBillingActions();
  const isPro = user?.plan === 'pro';

  const [loading, setLoading] = useState(false);
  const [alerts, setAlerts] = useState<SpendAlert[]>([]);
  const [suggestions, setSuggestions] = useState<SpendAlertSuggestion[]>([]);
  const [channels, setChannels] = useState<ChannelReadiness | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [proGate, setProGate] = useState<AnalyzeQuotaInfo | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<SpendAlert | null>(null);
  const [seed, setSeed] = useState<SpendAlertModalSeed | undefined>();

  const openProGate = useCallback(() => {
    setProGate({
      title: 'Pro unlocks spend alerts',
      message:
        'Get coached before your usual Daily spend windows — dining, subscriptions, shopping — via push or email.',
      upgradeRequired: true,
    });
  }, []);

  const load = useCallback(async () => {
    if (!isAuthenticated) {
      setAlerts([]);
      setSuggestions([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [listResult, sugResult] = await Promise.allSettled([
        listSpendAlerts(),
        suggestSpendAlerts(new Date().getTimezoneOffset()),
      ]);
      if (listResult.status === 'fulfilled') {
        setAlerts(listResult.value.alerts ?? []);
        setChannels(listResult.value.channels ?? null);
      } else {
        setAlerts([]);
      }
      if (sugResult.status === 'fulfilled') {
        setSuggestions(sugResult.value.suggestions ?? []);
        setChannels((prev) => sugResult.value.channels ?? prev);
      } else {
        setSuggestions([]);
      }
      if (listResult.status === 'rejected' && sugResult.status === 'rejected') {
        const err = listResult.reason;
        setError(err instanceof ApiError ? err.message : 'Could not load alerts');
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load alerts');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => subscribeSpendAlertsChanged(() => void load()), [load]);

  const openCreate = (nextSeed?: SpendAlertModalSeed) => {
    if (!isPro) {
      openProGate();
      return;
    }
    setEditing(null);
    setSeed(
      nextSeed ?? {
        categorySlug,
        categoryLabel,
        intent: intentFromCategorySlug(categorySlug),
      },
    );
    setModalOpen(true);
  };

  const openEdit = (alert: SpendAlert) => {
    if (!isPro) {
      openProGate();
      return;
    }
    setEditing(alert);
    setSeed(undefined);
    setModalOpen(true);
  };

  const toggleEnabled = async (alert: SpendAlert) => {
    if (!isPro) {
      openProGate();
      return;
    }
    setBusyId(alert.id);
    setToast(null);
    try {
      await patchSpendAlert(alert.id, { enabled: !alert.enabled });
      notifySpendAlertsChanged();
      setToast(alert.enabled ? 'Alert paused' : 'Alert back on');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not update alert');
    } finally {
      setBusyId(null);
    }
  };

  const removeAlert = async (alert: SpendAlert) => {
    if (!isPro) {
      openProGate();
      return;
    }
    setBusyId(alert.id);
    setToast(null);
    try {
      await deleteSpendAlert(alert.id);
      notifySpendAlertsChanged();
      setToast('Alert cancelled');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not cancel alert');
    } finally {
      setBusyId(null);
    }
  };

  if (!isAuthenticated) return null;

  const active = alerts.filter((a) => a.enabled);
  const paused = alerts.filter((a) => !a.enabled);

  return (
    <View style={styles.wrap}>
      <View style={styles.headerRow}>
        <Text style={styles.heading}>Cut-spend alerts</Text>
        {!isPro ? <Text style={styles.proHint}>Pro</Text> : null}
      </View>
      <Text style={styles.sub}>
        Mapped to Daily spend patterns — get coached before the window, not after the charge.
      </Text>

      {loading && alerts.length === 0 && suggestions.length === 0 ? (
        <ActivityIndicator color={colors.accent} style={styles.loader} />
      ) : null}
      {error ? <Text style={styles.error}>{error}</Text> : null}

      {active.length > 0 ? (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Your alerts</Text>
          {active.map((a) => (
            <AlertManageRow
              key={a.id}
              alert={a}
              busy={busyId === a.id}
              onEdit={() => openEdit(a)}
              onPause={() => void toggleEnabled(a)}
              onCancel={() => void removeAlert(a)}
            />
          ))}
        </View>
      ) : null}

      {paused.length > 0 ? (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Paused</Text>
          {paused.map((a) => (
            <AlertManageRow
              key={a.id}
              alert={a}
              busy={busyId === a.id}
              onEdit={() => openEdit(a)}
              onPause={() => void toggleEnabled(a)}
              onCancel={() => void removeAlert(a)}
            />
          ))}
        </View>
      ) : null}

      {suggestions.length > 0 ? (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>AI ideas from your spend</Text>
          {suggestions.map((s) => {
            const key = `${s.categorySlug}-${s.dayOfWeek}-${s.hourLocal}`;
            const timing = s.hourInferred
              ? `${s.dayLabel}s · time estimated (uploads have no clock)`
              : `${s.dayLabel}s around ${formatAlertHour(s.hourLocal)}`;
            return (
              <Pressable
                key={key}
                onPress={() => openCreate(seedFromSuggestion(s))}
                style={styles.card}
                accessibilityRole="button"
                accessibilityLabel={`${s.categoryLabel} alert idea`}
              >
                <View style={styles.cardTop}>
                  <View style={styles.cardTextCol}>
                    <Text style={styles.cardTitle} numberOfLines={1}>
                      {s.categoryLabel} · {s.dayLabel}s
                    </Text>
                    <Text style={styles.cardBody} numberOfLines={3}>
                      {s.reason}
                    </Text>
                    <Text style={styles.cardMeta} numberOfLines={2}>
                      {timing} · avg ${Math.round(s.avgAmount)} · {s.sampleCount}{' '}
                      trips · {s.sharePct}% of recent spend
                    </Text>
                  </View>
                  <Text style={styles.ctaText}>
                    {s.alreadyEnabled ? (isPro ? 'On' : 'Pro') : isPro ? 'Set' : 'Pro →'}
                  </Text>
                </View>
              </Pressable>
            );
          })}
        </View>
      ) : !loading ? (
        <Text style={styles.empty}>
          More Daily spend history unlocks ideas. Or set a custom alert for a goal.
        </Text>
      ) : null}

      <GlassButton
        variant="secondary"
        size="sm"
        onPress={() => openCreate()}
        accessibilityLabel="Set custom spend alert"
      >
        {isPro ? 'Set alert…' : 'Set alert with Pro →'}
      </GlassButton>

      {toast ? <Text style={styles.toast}>{toast}</Text> : null}
      <Text style={styles.sourceHint}>
        AI ranks real trips from the last ~90 days. Clock times are used only when
        your uploads include them — otherwise we nudge that weekday, not a made-up hour.
      </Text>

      <SetSpendAlertModal
        visible={modalOpen}
        onClose={() => setModalOpen(false)}
        editing={editing}
        seed={seed}
        channels={channels}
        onSaved={() => {
          setToast(editing ? 'Alert updated' : 'Alert saved');
          void load();
        }}
      />

      <AnalyzeQuotaModal
        visible={Boolean(proGate)}
        info={proGate}
        onDismiss={() => setProGate(null)}
        upgradeBusy={upgradeBusy}
        source="spend_alert_suggestions"
        onUpgrade={() => {
          setProGate(null);
          void upgradeToPro('spend_alert_suggestions');
        }}
      />
    </View>
  );
}

function AlertManageRow({
  alert,
  busy,
  onEdit,
  onPause,
  onCancel,
}: {
  alert: SpendAlert;
  busy: boolean;
  onEdit: () => void;
  onPause: () => void;
  onCancel: () => void;
}) {
  return (
    <View style={styles.manageCard}>
      <Pressable onPress={onEdit} style={styles.manageMain} accessibilityRole="button">
        <Text style={styles.cardTitle} numberOfLines={1}>
          {intentLabel(alert.intent)} · {alert.categoryLabel}
        </Text>
        <Text style={styles.cardMeta} numberOfLines={2}>
          {alert.dayLabel}s · warn {alert.leadHours}h before {formatAlertHour(alert.hourLocal)}
          {!alert.enabled ? ' · paused' : ''}
        </Text>
      </Pressable>
      <View style={styles.manageActions}>
        <Pressable
          onPress={onEdit}
          disabled={busy}
          style={styles.miniBtn}
          accessibilityLabel="Update alert"
        >
          <Text style={styles.miniBtnText}>Edit</Text>
        </Pressable>
        <Pressable
          onPress={onPause}
          disabled={busy}
          style={styles.miniBtn}
          accessibilityLabel={alert.enabled ? 'Pause alert' : 'Resume alert'}
        >
          <Text style={styles.miniBtnText}>{alert.enabled ? 'Pause' : 'Resume'}</Text>
        </Pressable>
        <Pressable
          onPress={onCancel}
          disabled={busy}
          style={styles.miniBtnDanger}
          accessibilityLabel="Cancel alert"
        >
          <Text style={styles.miniBtnDangerText}>Cancel</Text>
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    marginTop: spacing.sm,
    gap: 8,
    paddingTop: spacing.sm,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.glassBorder,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  heading: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  sub: {
    ...typography.caption,
    fontSize: 12,
    color: colors.textMuted,
    lineHeight: 16,
  },
  proHint: {
    ...typography.caption,
    fontSize: 11,
    color: colors.accent,
    fontWeight: '700',
  },
  loader: { marginVertical: 4 },
  empty: {
    ...typography.caption,
    fontSize: 12,
    color: colors.textMuted,
    lineHeight: 16,
  },
  section: { gap: 6 },
  sectionTitle: {
    ...typography.caption,
    fontSize: 11,
    fontWeight: '700',
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginTop: 4,
  },
  card: {
    gap: 6,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: radii.md,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.glassBorder,
    backgroundColor: 'rgba(255,255,255,0.03)',
  },
  manageCard: {
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: radii.md,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.glassBorder,
    backgroundColor: 'rgba(255,255,255,0.03)',
  },
  manageMain: { gap: 2 },
  manageActions: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  miniBtn: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: radii.pill,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.glassBorder,
  },
  miniBtnText: {
    ...typography.caption,
    fontSize: 11,
    fontWeight: '700',
    color: colors.accent,
  },
  miniBtnDanger: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: radii.pill,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: 'rgba(255,100,100,0.35)',
  },
  miniBtnDangerText: {
    ...typography.caption,
    fontSize: 11,
    fontWeight: '700',
    color: colors.danger,
  },
  cardTop: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
  },
  cardTextCol: { flex: 1, minWidth: 0, gap: 2 },
  cardTitle: {
    ...typography.body,
    fontSize: 14,
    fontWeight: '700',
    color: colors.text,
  },
  cardBody: {
    ...typography.caption,
    fontSize: 12,
    color: colors.text,
    lineHeight: 16,
  },
  cardMeta: {
    ...typography.caption,
    fontSize: 11,
    color: colors.textMuted,
    fontVariant: ['tabular-nums'],
  },
  ctaText: {
    ...typography.caption,
    fontWeight: '800',
    color: colors.accent,
    paddingTop: 4,
  },
  error: { ...typography.caption, color: colors.danger },
  toast: { ...typography.caption, color: colors.accent, fontWeight: '600' },
  sourceHint: {
    ...typography.caption,
    fontSize: 11,
    color: colors.textMuted,
    marginTop: 2,
  },
});
